<template>
  <FormField
    :label="label"
    :labelFor="defaultId"
    :error="error"
    :success="success"
    :showErrorFeedbackMessage="showErrorFeedbackMessage"
    :showSuccessFeedbackMessage="showSuccessFeedbackMessage"
    :feedbackMessage="feedbackMessage"
    :infoIcon="infoIcon"
    :infoText="infoText"
    :class="{ disabled: disabled }"
    :tooltipMinWidth="tooltipMinWidth"
    :size="size"
  >
    <template>
      <div class="input">
        <input
          :id="defaultId"
          :type="type"
          :placeholder="placeholder"
          :name="name"
          :value="value"
          :required="required"
          :readonly="readonly"
          :maxlength="maxlength"
          :disabled="disabled"
          :step="step"
          :class="{ 'input--field__error': error }"
          class="input--field"
          @input="$emit('input', $event.target.value)"
          @blur="$emit('blur', $event)"
          @focus="$emit('focus', $event)"
        />
        <span
          v-if="sufix"
          class="input--sufix"
          :class="{ disabled: disabled }"
          >{{ sufix }}</span
        >
      </div>
      <!-- LIMPAR CAMPO -->
      <transition name="fade">
        <button
          v-if="(value || typeof value === 'number') && !error && !success"
          class="input--clear"
          type="button"
          tabindex="-1"
          @click="$emit('input', '')"
        >
          {{ clearButtonText }}
        </button>
      </transition>

      <!-- CONTADOR DE CARACTERES -->
      <PmwParagraph
        v-if="showCounter"
        size="paragraph-40"
        class="input--counter"
      >
        {{ valueLength }}/{{ maxlength }} {{ counterText }}
      </PmwParagraph>
    </template>
  </FormField>
</template>

<script>
/**
 * Componente de input padrão para formulários
 */
import { getRandomInt } from '@/helpers/math'
import FormField from '../patterns/FormField'
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph'

export default {
  name: 'FormInput',
  components: {
    FormField,
    PmwParagraph
  },
  props: {
    /** Label do campo */
    label: {
      type: String,
      default: 'Label do input'
    },
    /** Placeholder do campo */
    placeholder: {
      type: String,
      default: 'Insira o conteúdo'
    },
    /** Tipo do campo */
    type: {
      type: String,
      default: 'text'
    },
    /** O atributo step especifica o intervalo entre números válidos em um elemento input */
    step: {
      type: String,
      default: null
    },
    /** Se o campo está com erro de validação */
    error: {
      type: Boolean,
      default: false
    },
    /** Se o campo passou na validação */
    success: {
      type: Boolean,
      default: false
    },
    /** Se deve mostrar uma mensagem de feedback caso ocorra erro */
    showErrorFeedbackMessage: {
      type: Boolean,
      default: false
    },
    /** Se deve mostrar uma mensagem de feedback caso ocorra sucesso */
    showSuccessFeedbackMessage: {
      type: Boolean,
      default: false
    },
    /** Texto da mensagem de feedback */
    feedbackMessage: {
      type: String,
      default: 'Campo obrigatório'
    },
    /** Nome do campo */
    name: {
      type: String,
      default: ''
    },
    /** Valor do campo */
    value: {
      type: [String, Number, Object, Array],
      default: ''
    },
    /** Se é obrigatório */
    required: {
      type: Boolean,
      default: false
    },
    /** Se é apenas leitura */
    readonly: {
      type: Boolean,
      default: false
    },
    /** Limite de caracteres (se necessário limitar) */
    maxlength: {
      type: [Number, String],
      default: null
    },
    /** Se deve apresentar um ícone de informação sobre o campo */
    infoIcon: {
      type: Boolean,
      default: false
    },
    /** Texto de informação */
    infoText: {
      type: String,
      default: 'Informação sobre o campo'
    },
    /** Sufixo que aparecerá após o valor do input */
    sufix: {
      type: String,
      default: ''
    },
    /** Texto do botão limpar */
    clearButtonText: {
      type: String,
      default: 'Limpar'
    },
    /**
     * Se deve desabilitar o input
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /** largura mínima da tooltip */
    tooltipMinWidth: {
      type: [Number, String],
      default: 150
    },
    /**
     * Largura do campo
     * small, large, fluid
     */
    size: {
      type: String,
      default: 'fluid'
    },

    /** Se deve mostrar o contador de caracteres */
    showCounter: {
      type: Boolean,
      default: false
    },

    /** Texto usado no contador de caracteres */
    counterText: {
      type: String,
      default: 'caracteres'
    }
  },
  data() {
    return {
      defaultId: `form-input-${getRandomInt(0, 99999)}`
    }
  },

  computed: {
    /** Retorna a quantidade de caracteres digitados */
    valueLength() {
      return this.value ? this.value.length : 0
    }
  }
}
</script>

<style lang="scss" scoped>
.input {
  display: flex;
  position: relative;

  &--counter {
    position: absolute;
    right: 0;
    top: calc(100% + 3px);
    color: pmw-colors('grey-70');
  }

  &--sufix {
    position: absolute;
    top: 50%;
    right: 0;
    font-size: font-size('size-medium');
    transform: translateY(-50%);
  }

  &--field {
    display: block;
    height: $input-height;
    padding: 1px 0;
    line-height: 21px;
    width: 100%;
    border: none;
    border-bottom: 1px solid $gray-darkest;
    font-size: font-size('size-medium');
    font-weight: font-weight('weight-normal');

    &[type='number']::-webkit-inner-spin-button,
    &[type='number']::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    @include placeholder {
      font-size: font-size('size-medium');
      font-weight: font-weight('weight-light');
      color: $gray-mid;
    }

    &:focus {
      border-color: $blue-2;
      border-bottom-width: 2px;
    }

    &:disabled {
      background-color: transparent;
      border-bottom: 1px solid $gray-mid;
    }

    &__error {
      border-bottom-color: $error;
      border-bottom-width: 2px;
    }
  }

  &--clear {
    @include reset-button;
    position: absolute;
    left: 0;
    top: calc(100% + 5px);
    line-height: 14px;
    font-size: font-size('size-x-small');
    color: $blue;
    z-index: 1;
  }
}
</style>
